import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Paper, Stack, Typography,
} from '@mui/material';
import { accountAction, accountKeys, savePassword, saveProfile, useAccount, useAccountSessions } from 'api/account';
import { extractApiError } from 'api/errors';
import { useAuth } from 'hooks/useAuth';
import AccountField from './AccountField';
import PasswordChangeDialog from './PasswordChangeDialog';
import { formatDate } from 'i18n/format';

const messages = () => ({
  invalid_password: translate('shell.invalidPassword'),
  invalid_code: translate('shell.invalidCode'),
  invalid_challenge: translate('shell.invalidChallenge'),
  email_unverified: translate('shell.emailUnverified'),
  email_required: translate('shell.emailRequiredPrimary'),
  codes_unavailable: translate('shell.codesUnavailable'),
});
const errorText = (error) => error.response?.data?.current_password
  ? translate('shell.currentPasswordIncorrect')
  : messages()[error.response?.data?.code] || extractApiError(error, translate('shell.saveChangesFailed'));
const dateText = (value) => formatDate(value, { dateStyle: 'medium', timeStyle: 'short' });

function Card({ title, children }) {
  useTranslation();
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>{title}</Typography>
      {children}
    </Paper>
  );
}

// Mounted only while open: passwords/codes are discarded when the dialog closes.
function ProofDialog({ title, label = translate('shell.confirm'), mfaEnabled, onSubmit, onClose }) {
  const { t: translate } = useTranslation();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await onSubmit({ current_password: password, ...(mfaEnabled ? { code } : {}) });
      onClose();
    } catch (err) { setError(errorText(err)); }
    finally { setBusy(false); setCode(''); }
  };
  return (
    <Dialog open onClose={() => { if (!busy) onClose(); }} maxWidth="xs" fullWidth>
      <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <AccountField label={translate('shell.currentPassword')} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus inputProps={{ maxLength: 4096 }} />
            {mfaEnabled && <AccountField label={translate('shell.authRecoveryCode')} autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required inputProps={{ maxLength: 32 }} />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>{translate('shell.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={busy}>{busy ? translate('shell.savingAccount') : label}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function ProfileSection({ account, refresh }) {
  const { t: translate } = useTranslation();
  const [name, setName] = useState(account.user.name || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError(''); setSaved(false);
    try { await saveProfile(name.trim()); refresh(); setSaved(true); }
    catch (err) { setError(errorText(err)); }
    finally { setBusy(false); }
  };
  return (
    <Card title={translate('shell.profile')}>
      <Box component="form" onSubmit={submit}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">{translate('shell.profileHint')}</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {saved && <Alert severity="success">{translate('shell.nameSaved')}</Alert>}
          <AccountField label={translate('shell.name')} value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} required inputProps={{ maxLength: 255 }} />
          <AccountField label={translate('shell.primaryEmail')} value={account.user.email} InputProps={{ readOnly: true }} helperText={translate('shell.emailManageHint')} />
          <Box><Button type="submit" variant="contained" disabled={busy || !name.trim() || name.trim() === account.user.name}>{translate('shell.saveName')}</Button></Box>
        </Stack>
      </Box>
    </Card>
  );
}

function RecoveryCodes({ codes, onHide }) {
  const { t: translate } = useTranslation();
  if (!codes?.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography fontWeight={600}>{translate('shell.recoveryCodes')}</Typography>
      <Typography variant="body2" color="text.secondary">{translate('shell.saveRecoveryCodes')}</Typography>
      <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '1rem', lineHeight: 1.8 }}>{codes.join('\n')}</Box>
      <Button onClick={onHide}>{translate('shell.hideCodes')}</Button>
    </Paper>
  );
}

function SecuritySection({ account, refresh }) {
  const { t: translate } = useTranslation();
  const [dialog, setDialog] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const start = async (proof) => {
    setCodes([]); setError(''); setNotice('');
    setEnrollment(await accountAction('totp/begin', proof));
  };
  const confirm = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const result = await accountAction('totp/confirm', { enrollment_token: enrollment.enrollment_token, code });
      setEnrollment(null); setCodes(result.recovery_codes); refresh(); setNotice(translate('shell.mfaEnabledNotice'));
    } catch (err) { setError(errorText(err)); }
    finally { setBusy(false); setCode(''); }
  };
  const actions = {
    password: { title: translate('shell.changePassword'), submit: async (proof) => { await savePassword(proof); refresh(); setNotice(translate('shell.passwordChanged')); } },
    enroll: { title: translate('shell.connectAuthenticator'), submit: start },
    deactivate: { title: translate('shell.disableMfa'), submit: async (proof) => { await accountAction('totp/deactivate', proof); setCodes([]); refresh(); setNotice(translate('shell.mfaDisabledNotice')); } },
    view: { title: translate('shell.showRecoveryCodes'), submit: async (proof) => { const result = await accountAction('recovery-codes/view', proof); setCodes(result.recovery_codes); } },
    regenerate: { title: translate('shell.regenerateRecoveryCodes'), submit: async (proof) => { const result = await accountAction('recovery-codes/regenerate', proof); setCodes(result.recovery_codes); refresh(); } },
  };
  return (
    <Stack spacing={2}>
      {notice && <Alert severity="success">{notice}</Alert>}
      <Card title={translate('shell.password')}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{translate(account.mfa_enabled ? 'shell.passwordProofMfa' : 'shell.passwordProof')}</Typography>
        <Button variant="outlined" onClick={() => setDialog('password')}>{translate('shell.changePassword')}</Button>
      </Card>
      <Card title={translate('shell.mfa')}>
        <Stack spacing={2} alignItems="flex-start">
          <Chip label={account.mfa_enabled ? translate('shell.enabled') : translate('shell.notEnabled')} color={account.mfa_enabled ? 'success' : 'default'} />
          <Typography variant="body2" color="text.secondary">{translate('shell.mfaHint')}</Typography>
          {!enrollment && <Button variant="outlined" onClick={() => setDialog(account.mfa_enabled ? 'deactivate' : 'enroll')}>{account.mfa_enabled ? translate('shell.disable2fa') : translate('shell.enable2fa')}</Button>}
          {enrollment && <Box component="form" onSubmit={confirm} sx={{ width: '100%' }}>
            <Stack spacing={2} alignItems="flex-start">
              <Typography variant="body2">{translate('shell.scanQr')}</Typography>
              <Box component="img" alt={translate('shell.qrAlt')} src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(enrollment.qr_svg)}`} sx={{ width: 208, height: 208, bgcolor: '#fff', p: 1, borderRadius: 1 }} />
              <AccountField label={translate('shell.manualSetupKey')} value={enrollment.secret} InputProps={{ readOnly: true }} />
              {error && <Alert severity="error">{error}</Alert>}
              <AccountField label={translate('shell.appCode')} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputProps={{ maxLength: 32 }} required />
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" disabled={busy}>{translate('shell.confirmCode')}</Button>
                <Button disabled={busy} onClick={() => { setEnrollment(null); setCode(''); setError(''); }}>{translate('shell.cancel')}</Button>
              </Stack>
            </Stack>
          </Box>}
          {account.mfa_enabled && <>
            <Divider flexItem />
            <Typography variant="subtitle2">{translate('shell.recoveryCodes')}</Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {account.has_recovery_codes && <Button onClick={() => setDialog('view')}>{translate('shell.showCodes')}</Button>}
              <Button onClick={() => setDialog('regenerate')}>{translate('shell.regenerateCodes')}</Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">{translate('shell.regenerateHint')}</Typography>
          </>}
        </Stack>
      </Card>
      <RecoveryCodes codes={codes} onHide={() => setCodes([])} />
      {dialog === 'password'
        ? <PasswordChangeDialog policy={account.password_policy} mfaEnabled={account.mfa_enabled} onSubmit={actions.password.submit} onClose={() => setDialog(null)} />
        : dialog && <ProofDialog title={actions[dialog].title} mfaEnabled={account.mfa_enabled} onSubmit={actions[dialog].submit} onClose={() => setDialog(null)} />}
    </Stack>
  );
}

function EmailSection({ account, refresh }) {
  const { t: translate } = useTranslation();
  const [email, setEmail] = useState('');
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const resend = async (address) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await accountAction('emails/resend', { email: address });
      setNotice(result.verification_sent ? translate('shell.verificationSent') : translate('shell.verificationRecent'));
      refresh();
    } catch (err) { setError(errorText(err)); }
    finally { setBusy(false); }
  };
  const submit = async (proof) => {
    await accountAction(`emails/${dialog.action}`, { ...proof, email: dialog.email });
    setEmail(''); refresh(); setNotice(translate('shell.emailsUpdated'));
  };
  const titles = { add: translate('shell.addEmail'), primary: translate('shell.makeEmailPrimary'), remove: translate('shell.removeEmail') };
  return (
    <Card title={translate('shell.email')}>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {notice && <Alert severity="success">{notice}</Alert>}
        {account.emails.map((address) => <Box key={address.email}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography sx={{ overflowWrap: 'anywhere' }}>{address.email}</Typography>
            {address.primary && <Chip size="small" label={translate('shell.primary')} />}
            <Chip size="small" label={address.verified ? translate('shell.verified') : translate('shell.unverified')} color={address.verified ? 'success' : 'default'} />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
            {!address.verified && <Button size="small" disabled={busy} onClick={() => resend(address.email)}>{translate('shell.sendVerification')}</Button>}
            {address.verified && !address.primary && <Button size="small" onClick={() => setDialog({ action: 'primary', email: address.email })}>{translate('shell.makePrimary')}</Button>}
            {!address.primary && <Button size="small" color="error" onClick={() => setDialog({ action: 'remove', email: address.email })}>{translate('shell.remove')}</Button>}
          </Stack>
        </Box>)}
        <Divider />
        <Box component="form" onSubmit={(event) => { event.preventDefault(); setDialog({ action: 'add', email }); }}>
          <Stack spacing={2}>
            <AccountField label={translate('shell.newEmail')} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={account.mfa_enabled} inputProps={{ maxLength: 254 }} />
            {account.mfa_enabled && <Typography variant="body2" color="text.secondary">{translate('shell.disableMfaForEmail')}</Typography>}
            <Box><Button type="submit" variant="outlined" disabled={!email || account.mfa_enabled}>{translate('shell.addAddress')}</Button></Box>
          </Stack>
        </Box>
      </Stack>
      {dialog && <ProofDialog title={`${titles[dialog.action]}: ${dialog.email}`} mfaEnabled={account.mfa_enabled} onSubmit={submit} onClose={() => setDialog(null)} />}
    </Card>
  );
}

function SessionsSection({ account }) {
  const { t: translate } = useTranslation();
  const { data, isLoading, error, refetch } = useAccountSessions();
  const { logout } = useAuth();
  const [dialog, setDialog] = useState(null);
  const submit = async (proof) => {
    const result = await accountAction('sessions/revoke', { ...proof, ...dialog });
    if (result.signed_out) logout();
    else refetch();
  };
  return (
    <Card title={translate('shell.activeSessions')}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">{translate('shell.sessionsHint')}</Typography>
        {isLoading && <CircularProgress size={24} />}
        {error && <Alert severity="error">{errorText(error)}</Alert>}
        {data?.sessions.map((session) => <Paper key={session.id} variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            {session.is_current && <Chip size="small" label={translate('shell.currentSession')} color="success" sx={{ alignSelf: 'flex-start' }} />}
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{session.user_agent || translate('shell.unknownDevice')}</Typography>
            <Typography variant="body2" color="text.secondary">{translate('shell.sessionLastSeen', { ip: session.ip || '—', date: dateText(session.last_seen_at) })}</Typography>
            <Typography variant="body2" color="text.secondary">{translate('shell.sessionCreated', { date: dateText(session.created_at) })}</Typography>
            <Box><Button color="error" size="small" onClick={() => setDialog({ scope: 'one', session_id: session.id })}>{translate('shell.endSession')}</Button></Box>
          </Stack>
        </Paper>)}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button onClick={() => refetch()}>{translate('shell.refresh')}</Button>
          <Button onClick={() => setDialog({ scope: 'others' })}>{translate('shell.endOthers')}</Button>
          <Button color="error" onClick={() => setDialog({ scope: 'all' })}>{translate('shell.signOutAll')}</Button>
        </Stack>
      </Stack>
      {dialog && <ProofDialog title={translate('shell.endSelectedSessions')} label={translate('shell.end')} mfaEnabled={account.mfa_enabled} onSubmit={submit} onClose={() => setDialog(null)} />}
    </Card>
  );
}

export default function AccountSettings({ section }) {
  const { t: translate } = useTranslation();
  const { data, isLoading, error, refetch } = useAccount();
  const qc = useQueryClient();
  const { logout } = useAuth();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: accountKeys.status });
    qc.invalidateQueries({ queryKey: accountKeys.sessions });
    qc.invalidateQueries({ queryKey: ['auth-me'] });
    qc.invalidateQueries({ queryKey: ['settings-members'] });
  };
  if (isLoading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error" action={<Button onClick={() => refetch()}>{translate('shell.retry')}</Button>}>{errorText(error)}</Alert>;
  if (!data) return null;
  if (data.session_upgrade_required) return <Alert severity="info" action={<Button onClick={logout}>{translate('shell.signInAgain')}</Button>}>{translate('shell.accountReauth')}</Alert>;
  if (data.has_usable_password === false && section !== 'profile') return <Alert severity="info" action={<Button component="a" href="/accounts/password/reset/">{translate('shell.setPassword')}</Button>}>{translate('shell.setPasswordHint')}</Alert>;
  return (
    <Box sx={{ maxWidth: 760 }}>
      {section === 'profile' && <ProfileSection account={data} refresh={refresh} />}
      {section === 'security' && <SecuritySection account={data} refresh={refresh} />}
      {section === 'email' && <EmailSection account={data} refresh={refresh} />}
      {section === 'sessions' && <SessionsSection account={data} />}
    </Box>
  );
}
